import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Toast.module.css';
import { cn } from '../cn';

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
  action?: ToastAction;
  durationMs?: number;
  /** Called when the toast leaves through its close control or timer. */
  onDismiss?: () => void;
}

interface ToastContextValue {
  push: (toast: Omit<Toast, 'id'>) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const dismissCallbacksRef = useRef(new Map<string, () => void>());

  const dismiss = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer !== undefined) clearTimeout(timer);
    timersRef.current.delete(id);
    const onDismiss = dismissCallbacksRef.current.get(id);
    dismissCallbacksRef.current.delete(id);
    setToasts((t) => t.filter((x) => x.id !== id));
    onDismiss?.();
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).slice(2);
      const full: Toast = { ...toast, id };
      setToasts((t) => [...t, full]);
      if (toast.onDismiss) dismissCallbacksRef.current.set(id, toast.onDismiss);
      const stickyVariants: ToastVariant[] = ['warning', 'danger'];
      const duration = toast.durationMs ?? (stickyVariants.includes(toast.variant ?? 'info') ? Infinity : 4000);
      if (duration !== Infinity) {
        timersRef.current.set(id, setTimeout(() => dismiss(id), duration));
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => () => {
    for (const timer of timersRef.current.values()) clearTimeout(timer);
    timersRef.current.clear();
    for (const onDismiss of dismissCallbacksRef.current.values()) {
      try { onDismiss(); } catch { /* owner teardown must not block siblings */ }
    }
    dismissCallbacksRef.current.clear();
  }, []);

  return (
    <ToastContext.Provider value={{ push, dismiss }}>
      {children}
      {/* On the body, beside the app rather than in it: a modal makes the app inert
          while it is open, and a toast it raises has to stay readable and
          clickable (TOADO-984). */}
      {typeof document !== 'undefined' && createPortal(
        <div className={styles.region} aria-live="polite" aria-atomic="false">
          {toasts.map((t) => (
            <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  return (
    <div className={cn(styles.toast, styles[`v-${toast.variant ?? 'info'}`])}>
      <div className={styles.accent} />
      <div className={styles.body}>
        <div className={styles.title}>{toast.title}</div>
        {toast.description && <div className={styles.desc}>{toast.description}</div>}
      </div>
      {toast.action && (
        <button type="button" className={styles.action} onClick={toast.action.onClick}>
          {toast.action.label}
        </button>
      )}
      <button type="button" className={styles.close} onClick={onDismiss} aria-label="Dismiss">×</button>
    </div>
  );
}
